import { useLayoutEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { Badge, Tooltip, TooltipContent, TooltipPortal, TooltipTrigger, cn } from '@atrvd/saas-ui'
import type { CreditCardSource } from '@/lib/db-tenant'
import { useClickableTooltip } from '@/components/table/useClickableTooltip'

const PRINTED_STATEMENT_BALANCE_HINT =
  'Printed statement total. The bank has not reported what remains.'

/** "Connected" (a linked card) or "Manual": where a card's figures come from. */
export function CreditCardSourceBadge({ source }: { source: CreditCardSource }) {
  switch (source) {
    case 'linked':
      return <Badge variant="success">Connected</Badge>
    case 'manual':
      return <Badge variant="muted">Manual</Badge>
    default: {
      const unknown: never = source
      throw new Error(`Unknown credit card source: ${String(unknown)}`)
    }
  }
}

interface ManuallyEditedValueProps {
  /** The figure as the column formats it. */
  text: string
  /** "Edited manually. From integration: …" */
  tooltip: string
}

/**
 * A table figure that a manual value replaced on a linked card: the value
 * plus a pencil whose tooltip names the integration's own figure.
 *
 * The tooltip opens on hover, focus, click or tap; its text is also `sr-only`
 * beside the figure so a screen reader reads both while browsing the table.
 */
export function ManuallyEditedValue({ text, tooltip }: ManuallyEditedValueProps) {
  const { openOnClick, ...tooltipState } = useClickableTooltip()
  return (
    <span className="inline-flex items-center gap-1.5">
      {text}
      <Tooltip {...tooltipState}>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex cursor-help text-primary" onClick={openOnClick}>
            <Pencil aria-hidden="true" className="h-3 w-3" />
            {/* Leading space: the accessible name concatenates adjacent nodes. */}
            <span className="sr-only">{` ${tooltip}`}</span>
          </span>
        </TooltipTrigger>
        {/* Portaled so the table's horizontal scroll wrapper cannot clip it. */}
        <TooltipPortal>
          <TooltipContent className="max-w-xs">
            {/* Radix also renders a hidden role="tooltip" copy; the sr-only text
                above is the one reading, so this one is hidden from AT. */}
            <span aria-hidden="true">{tooltip}</span>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </span>
  )
}

/**
 * A Statement Balance that is the printed statement total, not what remains.
 *
 * Muted, with the reason in a tooltip for sighted users (hover, focus, click or
 * tap) and as `sr-only` text so a screen reader reads it with the figure while
 * browsing the table, not only on focus.
 */
export function PrintedStatementBalance({ value }: { value: number }) {
  const { openOnClick, ...tooltipState } = useClickableTooltip()
  return (
    <Tooltip {...tooltipState}>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="cursor-help text-muted-foreground" onClick={openOnClick}>
          {formatCurrency(value)}
          {/* Leading space: the accessible name concatenates adjacent nodes. */}
          <span className="sr-only">{` ${PRINTED_STATEMENT_BALANCE_HINT}`}</span>
        </span>
      </TooltipTrigger>
      {/* Portaled so the table's horizontal scroll wrapper cannot clip it. */}
      <TooltipPortal>
        <TooltipContent className="max-w-xs">
          {/* Radix renders its children twice: visibly, and in a visually
              hidden role="tooltip" copy the trigger's aria-describedby points
              at. Hiding them from AT keeps the sr-only text the only reading. */}
          <span aria-hidden="true">{PRINTED_STATEMENT_BALANCE_HINT}</span>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}

/**
 * A card's note, clamped to two lines. When the clamp cuts it, the full note
 * opens in a tooltip on hover, focus, click or tap, and the cell becomes a tab
 * stop; a note that fits is plain text.
 *
 * No `sr-only` copy, unlike the cells above: the clamp is visual only, so a
 * screen reader already reads the whole note from the cell.
 */
export function CreditCardNote({ note }: { note: string }) {
  const { openOnClick, open, onOpenChange } = useClickableTooltip()
  const textRef = useRef<HTMLSpanElement>(null)
  const [clamped, setClamped] = useState(false)

  // Only layout knows whether the clamp cut the text; re-measured when the
  // column is resized. jsdom has no ResizeObserver (and no layout).
  useLayoutEffect(() => {
    const element = textRef.current
    if (!element) return
    const measure = () => setClamped(element.scrollHeight > element.clientHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [note])

  return (
    <Tooltip open={clamped && open} onOpenChange={onOpenChange}>
      <TooltipTrigger asChild>
        <span
          ref={textRef}
          tabIndex={clamped ? 0 : undefined}
          onClick={clamped ? openOnClick : undefined}
          className={cn('line-clamp-2 max-w-[240px] whitespace-pre-line break-words', clamped && 'cursor-help')}
        >
          {note}
        </span>
      </TooltipTrigger>
      {/* Portaled so the table's horizontal scroll wrapper cannot clip it. */}
      <TooltipPortal>
        <TooltipContent className="max-w-xs whitespace-pre-line break-words">
          {/* Radix also renders a hidden role="tooltip" copy the trigger is
              described by; the cell text already reads, so hide both copies. */}
          <span aria-hidden="true">{note}</span>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}
